import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem,
  Button,
  Divider
} from '@mui/material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer 
} from 'recharts';
import { getSignalHistory } from '../../services/api';

const PERIOD_OPTIONS = [
  { value: '1week', label: '1 неделя' },
  { value: '1month', label: '1 месяц' },
  { value: '3months', label: '3 месяца' },
  { value: '6months', label: '6 месяца' }
];

const convertPortNumber = (value) => {
  if (!value) return '-';
  const port = Math.floor((value - 16779009) / 256) + 1;
  const subPort = ((value - 16779009) % 256) + 1;
  return `${port}/${subPort}`;
};

const SignalDetails = () => {
  const { oltIp, portNumber } = useParams();
  const [data, setData] = useState([]);
  const [period, setPeriod] = useState('1month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchSignalHistory();
  }, [oltIp, portNumber, period]);

  const fetchSignalHistory = async () => {
    try {
      setLoading(true);
      const response = await getSignalHistory(oltIp, portNumber, period);
      const formattedData = response.map(item => ({
        ...item,
        signal_olt: item.signal_olt / 100,
        signal_onu: item.signal_onu / 100,
        date: new Date(item.date).toLocaleString()
      }));
      
      // Получаем description из первой записи
      if (formattedData.length > 0) {
        setDescription(formattedData[0].port_description || '');
      }
      
      setData(formattedData);
    } catch (err) {
      console.error('Error fetching signal history:', err);
      setError('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Дата', 'Сигнал OLT (dB)', 'Сигнал ONU (dB)'],
      ...data.map(item => [
        item.date,
        item.signal_olt.toFixed(2),
        item.signal_onu.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `signal_history_${oltIp}_${portNumber}.csv`;
    link.click();
  };

  if (loading) {
    return <Box p={3}>Загрузка...</Box>;
  }

  if (error) {
    return <Box p={3} color="error.main">{error}</Box>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">
            История сигналов для OLT {oltIp}, порт {convertPortNumber(parseInt(portNumber))}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography variant="subtitle1" color="text.secondary">
            {description || 'Нет описания'}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <FormControl size="small">
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIOD_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={handleExport}
            disabled={!data.length}
          >
            Экспорт CSV
          </Button>
        </Box>
      </Box>

      <Box height={400}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data}
            margin={{
              top: 30,    // Увеличили отступ сверху для легенды
              right: 30,
              left: 20,
              bottom: 90  // Увеличили отступ снизу для дат
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={{ 
                value: 'dB', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip />
            <Legend 
              verticalAlign="top"    // Размещаем легенду сверху
              height={36}            // Задаем высоту области легенды
              wrapperStyle={{
                paddingBottom: '20px'  // Добавляем отступ снизу от легенды
              }}
            />
            <Line 
              type="monotone" 
              dataKey="signal_olt" 
              name="Сигнал OLT" 
              stroke="#8884d8" 
              dot={false}
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="signal_onu" 
              name="Сигнал ONU" 
              stroke="#82ca9d" 
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default SignalDetails; 